import { useQuery } from "@apollo/client/react";
import { Button, Drawer, IconButton, Stack, styled, Typography } from "@mui/material";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import useAuth from "../../context/UserContext";
import {
  DownloadBundle,
  Event,
  Upload,
  AllUploadDownloadsDocument,
  AllUploadDownloadsQuery,
  AllUploadDownloadsQueryVariables,
  DownloadDocument,
  DownloadQuery,
  DownloadQueryVariables,
  FlowDownloadsDocument,
  FlowDownloadsQuery,
  FlowDownloadsQueryVariables,
} from "../../graphql/types";
import { toBundles } from "../common/FileDetailViews";
import { GqlError } from "../common/GqlError";
import IconCopy from "../common/IconCopy";
import { DownloadsDataGrid } from "../DownloadsDataGrid/DownloadsDataGrid";
import { initialVisibilityModelDownloadsDatagrid } from "../DownloadsDataGrid/DownloadsDataGridWithQuery";
import { UploadsDataGrid } from "../UploadsDataGrid/UploadsDataGrid";
import { initialVisibilityModelUploadsDatagrid } from "../UploadsDataGrid/UploadsDataGridWithQuery";

const DrawerContainer = styled(Stack)(({ theme }) => ({
  padding: "48px 24px 48px 20px",
  width: "80vw",
  height: "100vh",
  backgroundColor: theme.palette.surfaceLight20.main,
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  gap: "20px",
  borderTopLeftRadius: "12px",
  borderBottomLeftRadius: "12px",
}));

const EMPTY_DOWNLOAD_FILTER = {
  projects: [],
  importNames: [],
  bundleNames: [],
  search: "",
  status: "",
  reportRange: {
    start: undefined,
    end: undefined,
  },
  purpose: "",
  downloadedRange: {
    start: undefined,
    end: undefined,
  },
};

const EMPTY_UPLOAD_FILTER = {
  projects: [],
  exportNames: [],
  search: "",
  status: "",
  reportRange: {
    start: null,
    end: null,
  },
  purpose: "",
  uploadedRange: {
    start: null,
    end: null,
  },
};
const DownloadsDataGridWrapper = ({
  rows,
  loading,
  error,
}: {
  rows: DownloadBundle[];
  loading: boolean;
  error: Error | undefined;
}) => {
  const { user } = useAuth();

  if (error) return <GqlError error={error} />;

  return (
    <DownloadsDataGrid
      filter={EMPTY_DOWNLOAD_FILTER}
      rows={rows}
      onRowsScrollEnd={undefined}
      loading={loading}
      datagridPreferencesElement={user.datagridPreferences.downloadsFromEvent}
      initialVisibilityModel={initialVisibilityModelDownloadsDatagrid}
      preferenceElementToUpdate={"downloadsFromEvent"}
    />
  );
};

const DownloadDetail = ({ download_id }: { download_id: string }) => {
  const { loading, error, data } = useQuery<DownloadQuery, DownloadQueryVariables>(DownloadDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    variables: { id: download_id },
  });

  const rows: DownloadBundle[] = [];
  if (data) {
    rows.push(data.download as DownloadBundle);
  }
  return <DownloadsDataGridWrapper rows={rows} loading={loading} error={error} />;
};

const DownloadsForFlow = ({ flow_id }: { flow_id: string }) => {
  const { loading, error, data } = useQuery<FlowDownloadsQuery, FlowDownloadsQueryVariables>(FlowDownloadsDocument, {
    fetchPolicy: "cache-and-network",
    variables: { id: flow_id },
  });
  const rows: DownloadBundle[] = [];
  if (data) {
    data.flow_downloads?.edges?.forEach((edge) => {
      if (edge?.node) {
        rows.push(edge.node as DownloadBundle);
      }
    });
  }

  return <DownloadsDataGridWrapper rows={rows} loading={loading} error={error} />;
};

const Container = styled(Stack)({
  width: "100%",
  borderRadius: "8px",
  minHeight: "150px",
});

const UploadsDetails = ({ upload_id }: { upload_id: string }) => {
  const variables = { id: upload_id, first: 50 };
  const { user } = useAuth();
  const { loading, error, data } = useQuery<AllUploadDownloadsQuery, AllUploadDownloadsQueryVariables>(
    AllUploadDownloadsDocument,
    {
      fetchPolicy: "cache-and-network",
      variables,
    }
  );
  let bundles: DownloadBundle[] = [];
  let uploads: Upload[] = [];
  if (error) return <GqlError error={error} />;
  if (data) {
    bundles = data.uploads ? toBundles(data.uploads.edges.length > 0 ? data.uploads.edges[0].node.inputs : []) : [];
    uploads = data.uploads ? data.uploads.edges.map((edge) => edge.node as Upload) : [];
  }
  return (
    <>
      <Typography variant="heading.five">Uploads</Typography>
      <Container>
        <UploadsDataGrid
          onRowsScrollEnd={undefined}
          filter={EMPTY_UPLOAD_FILTER}
          loading={loading}
          rows={uploads}
          datagridPreferencesElement={user.datagridPreferences.uploadsFromEvent}
          preferenceElementToUpdate={"uploadsFromEvent"}
          initialVisibilityModel={initialVisibilityModelUploadsDatagrid}
        />
      </Container>
      <Typography variant="heading.five">Downloads</Typography>
      <Container>
        <DownloadsDataGridWrapper rows={bundles} loading={loading} error={error} />
      </Container>
    </>
  );
};

const EventsDrawer = ({ event, open, onClose }: { event?: Event; open: boolean; onClose: () => void }) => {
  const [showExpandedMessage, setShowExpandedMessage] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = () => {
    const container = containerRef.current;
    if (container) {
      setHasOverflow(container.scrollHeight > container.clientHeight);
    }
  };
  useEffect(() => {
    checkOverflow();
  }, []);
  if (!event) {
    return null;
  }
  if (!event) return null;
  // prettier-ignore
  const detailRowComponent = event.upload ? <UploadsDetails upload_id={event.upload.id} /> :
                               event.download ? <DownloadDetail download_id={event.download.id} /> :
                               event.flow ? <DownloadsForFlow flow_id={event.flow.id} /> :
                                   <Typography variant="heading.five">No elements</Typography>;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={(event: ChangeEvent) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <DrawerContainer>
        <IconButton
          onClick={onClose}
          sx={{
            width: "36px",
            height: "36px",
            justifyContent: "flex-end",
            ml: "auto",
          }}
        >
          <XIcon size="24px" />
        </IconButton>
        <Stack direction="row" sx={{ gap: "8px" }}>
          <Typography variant="heading.five">Event message</Typography>
          <IconCopy size="24px" clipboard={event.message} />
        </Stack>
        <Stack
          sx={(theme) => ({
            gap: "12px",
            borderRadius: "8px",
            minHeight: "66px",
            padding: "20px",
            backgroundColor: theme.palette.surfaceLight10.main,
          })}
        >
          <Stack
            sx={{
              height: showExpandedMessage ? "672px" : "auto",
              overflow: showExpandedMessage ? "auto" : "hidden",
              maxHeight: showExpandedMessage ? "672px" : "300px",
            }}
            ref={containerRef}
          >
            <Typography variant="body.small.regular">{event.message}</Typography>
          </Stack>
          {(hasOverflow || showExpandedMessage) && (
            <Button
              onClick={() => setShowExpandedMessage(!showExpandedMessage)}
              variant="outlined"
              sx={{
                width: "fit-content",
              }}
            >
              {showExpandedMessage ? "See less" : "See more"}
            </Button>
          )}
        </Stack>
        {detailRowComponent}
      </DrawerContainer>
    </Drawer>
  );
};

export { EventsDrawer };
